import React, { useEffect, useId } from 'react';
import { Button, Popper, Text } from '@fragments';
import { IconCoins } from '@tabler/icons-react';
import CreditsPanel from './components/CreditsPanel';
import useHoverPanel from '../../hooks/useHoverPanel';
import { formatCredits } from '../../data/payments';
import './styles.scss';

// `GET /studio/usage`: spendable credits; reserved ones are held by running operations.
// With payments on, hovering (or focusing, or tapping) the balance opens its panel:
// the balance split, credit packs and, on Standard, the upgrade to Pro.
export default function StudioCredits({ studio: s }) {
  const { t, credits } = s;
  const p = s.payments;
  const id = useId();
  const { control, trigger, panel, primary, open, setOpen, close, openPanel, hoverIn, hoverOut, onPanelKeyDown, onTriggerKeyDown } = useHoverPanel('bottom-end');
  const locale = s.locale.replace('_', '-');
  const show = () => openPanel(<CreditsPanel studio={s} id={id} panelRef={panel} primaryRef={primary} onKeyDown={onPanelKeyDown}
    onBuy={quantity => { close(); p.openCredits(quantity); }}
    onUpgrade={() => { close(); p.openUpgrade('pro'); }} />);
  // The panel content is handed to the Popper when it opens; refresh it while open.
  useEffect(() => { if (open) show(); }, [credits, p.plan, p.catalog, s.snapshot.usage]); // eslint-disable-line react-hooks/exhaustive-deps

  if (credits === null) return null;
  const amount = <span className="studio-credits__amount"><Text.label contrast={8}>{formatCredits(credits, locale)}</Text.label><Text.caption className="studio-credits__label" contrast={6}>{t('creditsLabel')}</Text.caption></span>;
  if (!p.enabled) return <div className="studio-credits" aria-label={t('credits', { count: credits })}><IconCoins aria-hidden="true" />{amount}</div>;
  return <Popper controlRef={control} config={{ activateOnHover: true }} onOpenChange={setOpen} paperSx={{
    width: 'min(320px, calc(100vw - 32px))', maxWidth: 'none',
    '& .popper-header': { padding: 0, border: 0 },
    // This popper holds a panel with its own controls, not an options list.
    '& > .MuiList-root': { display: 'none' },
  }}>
    <Button ref={trigger} className="studio-credits" size="small" color="overlay" variant="rounded" preventLoading startIcon={<IconCoins />}
      aria-label={t('credits', { count: credits })} aria-haspopup="dialog" aria-expanded={open} aria-controls={open ? id : undefined}
      onMouseEnter={() => hoverIn(show)} onMouseLeave={hoverOut} onFocus={show} onClick={show} onKeyDown={onTriggerKeyDown}>
      {amount}
    </Button>
  </Popper>;
}
